import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { z } from "zod";

dayjs.extend(utc);

/** Local calendar date, e.g. "09/10/2026". */
export const LOCAL_DATE_FORMAT = "MM/DD/YYYY";

/** Local date-time, e.g. "09/10/2026 3:52:00 PM". */
export const LOCAL_DATE_TIME_FORMAT = "MM/DD/YYYY h:mm:ss A";

/** Local date-time on a 24 hour clock, "09/25/2026 17:36:29" */
const LOCAL_DATE_TIME_24H_FORMAT = "MM/DD/YYYY HH:mm:ss";

/** UTC date-time, "2026-09-25 21:36:29 UTC" */
const UTC_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss [UTC]";

const UTC_OFFSET_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZoneName: "shortOffset",
});

/** ISO calendar date with no time component, e.g. "2024-01-15". */
const DATE_ONLY_REGEXP = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Format a YYYY-MM-DD calendar date from its parts (no timezone conversion).
 * Returns null when the string is not date-only; "" when parts are invalid.
 */
function formatDateOnlyString(value: string): string | null {
  const match = DATE_ONLY_REGEXP.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  // Local Date(y, m-1, d) validates the calendar day without UTC parsing.
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return "";
  }

  return `${match[2]}/${match[3]}/${match[1]}`;
}

/**
 * Format a calendar date for display (MM/DD/YYYY).
 * Date-only strings (YYYY-MM-DD) are formatted from their parts so the calendar
 * day is stable across timezones. Other values use local timezone conversion.
 * Returns "" for null/undefined/invalid values.
 */
export function formatLocalDate(
  value: string | Date | null | undefined,
): string {
  if (value == null) return "";
  if (typeof value === "string") {
    const dateOnly = formatDateOnlyString(value);
    if (dateOnly !== null) return dateOnly;
  }
  const date = dayjs(value);
  return date.isValid() ? date.format(LOCAL_DATE_FORMAT) : "";
}

/**
 * Format a value as a local date-time (MM/DD/YYYY h:mm:ss A).
 * Returns "" for null/undefined/invalid values.
 */
export function formatLocalDateTime(
  value: string | Date | null | undefined,
): string {
  if (value == null) return "";
  const date = dayjs(value);
  return date.isValid() ? date.format(LOCAL_DATE_TIME_FORMAT) : "";
}

/**
 * Format a timestamp in the browser time zone with its UTC offset,
 * "09/25/2026 17:36:29 (UTC-4)". Returns "" for null/undefined/invalid values.
 *
 * Use for business event timestamps, when users need the date and time something
 * happened: Requested At, Submitted At, Delivered At, Completed At.
 * @see https://onpointhealthdata.atlassian.net/wiki/spaces/BH/pages/3658383372/Intake+Redesign+UI+UX+Best+Practices#5.-Date-&-Time-Standards
 */
export function formatLocalDateTimeWithOffset(
  value: string | null | undefined,
): string {
  if (value == null) return "";
  const date = dayjs(value);
  if (!date.isValid()) return "";
  const offset = UTC_OFFSET_FORMAT.formatToParts(date.toDate())
    .find((part) => part.type === "timeZoneName")
    ?.value.replace("GMT", "UTC");
  const formatted = date.format(LOCAL_DATE_TIME_24H_FORMAT);
  return offset ? `${formatted} (${offset})` : formatted;
}

/**
 * Format a timestamp in UTC, "2026-09-25 21:36:29 UTC".
 * Returns "" for null/undefined/invalid values.
 *
 * Use for audit and system timestamps, which read the same for everyone:
 * Created At, Last Updated At, history pages and logs.
 * @see https://onpointhealthdata.atlassian.net/wiki/spaces/BH/pages/3658383372/Intake+Redesign+UI+UX+Best+Practices#5.-Date-&-Time-Standards
 */
export function formatUtcDateTime(value: string | null | undefined): string {
  if (value == null) return "";
  const date = dayjs.utc(value);
  return date.isValid() ? date.format(UTC_DATE_TIME_FORMAT) : "";
}

const YEAR_MONTH_REGEXP = /^(?<year>\d{4})-(?<month>0[1-9]|1[0-2])$/;

/** A 2-digit month, ranging from "01" to "12".  */
type TwoDigitMonth =
  | "01"
  | "02"
  | "03"
  | "04"
  | "05"
  | "06"
  | "07"
  | "08"
  | "09"
  | "10"
  | "11"
  | "12";

/** A year/month combination formatted as "YYYY-MM". */
export type YearMonth = `${bigint}-${TwoDigitMonth}`;

/** A Zod schema that can be used to validate "YYYY-MM" strings and parse them into YearMonth objects. */
export const YearMonthSchema = z
  .string()
  .regex(YEAR_MONTH_REGEXP)
  .transform((val) => val as YearMonth);

/** Converts the dayjs object to a "YYYY-MM" string (using UTC).  **/
export function dayJsToYearMonth(dayjs: Dayjs): YearMonth {
  return dayjs.format("YYYY-MM") as YearMonth;
}

/** Converts a YearMonth to a Dayjs. */
export function yearMonthToDayjs(yearMonth: YearMonth): Dayjs {
  const [year, month] = yearMonth.split("-");
  return dayjs()
    .year(Number(year))
    .month(Number(month) - 1)
    .date(1);
}

/**
 * Convert from "YYYY-MM" (used in JSON bodies) to "YYYYMM" (sometimes used to display months in filenames).
 * @param yearMonth the calendar year and month
 * @returns the year/month formatted as "YYYYMM" (i.e. without the hyphen).
 */
export function convertYearMonthToFileNameFormat(yearMonth: YearMonth): string {
  const matches = yearMonth.match(YEAR_MONTH_REGEXP);
  const errorMsg = `Invalid yearMonth format: '${yearMonth}'. Expected format: YYYY-MM.`;
  if (
    matches === null ||
    matches.length !== 3 ||
    typeof matches[1] !== "string" ||
    typeof matches[2] !== "string"
  ) {
    throw new Error(errorMsg);
  }
  const year = matches[1];
  const month = matches[2];
  return `${year}${month}`;
}

/** 24-hour clock time, "09:00" or "23:59". */
export const TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Parses a strict "HH:mm" string, or null if invalid. */
export function parseTimeOfDay(
  value: string,
): { hour: number; minute: number } | null {
  const match = TIME_OF_DAY_PATTERN.exec(value);
  if (!match) return null;
  return { hour: Number(match[1]), minute: Number(match[2]) };
}

/**
 * Convert a stored "HH:mm" string into a Dayjs value for MUI TimePicker.
 * Returns null when the string is not a valid 24-hour time.
 */
export function timeOfDayToDayjs(value: string): Dayjs | null {
  const parsed = parseTimeOfDay(value);
  if (!parsed) return null;
  return dayjs()
    .hour(parsed.hour)
    .minute(parsed.minute)
    .second(0)
    .millisecond(0);
}
